import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user data for development
const mockUser: User = {
  id: '1',
  email: 'admin@fastron.co.za',
  firstName: 'Thabo',
  lastName: 'Mokoena',
  role: 'admin',
  company: 'Fastron Motor Spares',
  phone: '+27 (11) 234-5678',
  address: {
    street: '123 Commissioner Street',
    city: 'Johannesburg',
    state: 'Gauteng',
    zipCode: '2001',
    country: 'South Africa'
  },
  taxId: '123456789',
  isEmailVerified: true,
  isTwoFactorEnabled: false,
  preferences: {
    currency: 'ZAR',
    currencySymbol: 'R',
    currencyDisplay: 'symbol',
    language: 'en',
    timezone: 'Africa/Johannesburg',
    dateFormat: 'DD/MM/YYYY',
    numberFormat: 'en-ZA'
  },
  createdAt: new Date('2024-01-01'),
  updatedAt: new Date('2024-01-01'),
  subscription: {
    id: 'sub_123',
    userId: '1',
    plan: 'pro',
    status: 'active',
    currentPeriodStart: new Date('2024-01-01'),
    currentPeriodEnd: new Date('2024-02-01'),
    cancelAtPeriodEnd: false,
    stripeSubscriptionId: 'sub_123456789',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01')
  }
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth token
    const token = localStorage.getItem('authToken');
    if (token) {
      // In a real app, validate token with backend
      setUser(mockUser);
    } else {
      // For development purposes, automatically set mock user
      setUser(mockUser);
    }
    console.log('AuthContext useEffect - setting user:', mockUser);
    setLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation
      if (email === 'admin@fastron.co.za' && password === 'password') {
        const token = 'mock-jwt-token';
        localStorage.setItem('authToken', token);
        setUser(mockUser);
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock registration
      const newUser: User = {
        ...mockUser,
        id: '2',
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: 'client',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      const token = 'mock-jwt-token';
      localStorage.setItem('authToken', token);
      setUser(newUser);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
  };

  const forgotPassword = async (email: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock password reset email sent
      console.log('Password reset email sent to:', email);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock password reset
      console.log('Password reset with token:', token);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
